import { context } from 'auth';
import { get, post } from 'fetch-api';
import { NBXStorage } from 'storage';

export const CARD_PARTNERSHIP_CODE = 'card:cardPartnershipCode';

let baseUrl = 'card';
const buildUrl = (account: any, user: any, path: string = '') =>
  `${baseUrl}/accounts/${account.id}/users/${user.id}${path}`;

const contexts = async () => {
  const account = await context.account();
  const user = await context.user();
  return { account, user };
};

export enum CardStatus {
  PENDING = 'PENDING',
  AVAILABLE = 'AVAILABLE',
  BLOCKED = 'BLOCKED'
}

export interface RegionalBlocks {
  southAmerica: boolean;
  home: boolean;
  africa: boolean;
  europe: boolean;
  asia: boolean;
  ecomm: boolean;
  northAmerica: boolean;
  oceania: boolean;
}

export interface Card {
  accountId: string;
  userId: string;
  cardId: string;
  type: string;
  status: CardStatus;
  cardNumber?: string;
  name?: string;
  expiration?: string;
  regionalBlocks: RegionalBlocks;
}

export const cardLast4 = (card: Card): string => card.cardNumber?.split(/_+/)[1] ?? '';

export interface TokenizeResponse {
  activationData: string;
}

export enum WalletVendor {
  GooglePay = 'GOOGLE_PAY',
  ApplePay = 'APPLE_PAY'
}

interface GooglePayPushProvisionParams {
  clientDeviceId: string;
  walletAccountId: string;
}

export interface ApplePayPushProvisionParams {
  certificates: [string];
  nonce: string;
  nonceSignature: string;
}

export interface ApplePayPushProvisionResponse {
  activationData: string;
  encryptedPassData: string;
  ephemeralPublicKey: string;
}

export interface GooglePayPushProvisionResponse {
  digitizationData: string;
}

export interface TokenizePushProvisionParams {
  cardId: string;
  vendor: WalletVendor;
  params: GooglePayPushProvisionParams | ApplePayPushProvisionParams;
}

export type TokenizePushProvisionResponse = ApplePayPushProvisionResponse | GooglePayPushProvisionResponse;

export interface TokenizationStartedParams {
  cardId: string;
  vendor: WalletVendor;
}

export const getCardPartnershipCodeFromLs = (): Promise<string | null> =>
  NBXStorage.getItem(CARD_PARTNERSHIP_CODE, { useLocalStorage: true });
export const setCardPartnershipCodeToLs = (cardPartnershipCode: string): Promise<void> =>
  NBXStorage.setItem(CARD_PARTNERSHIP_CODE, cardPartnershipCode, { useLocalStorage: true });
export const removeCardPartnershipCodeFromLs = (): void =>
  NBXStorage.removeItem(CARD_PARTNERSHIP_CODE, { useLocalStorage: true });

export interface CardPartnershipParams {
  cardPartnershipCode: string;
  onSuccess?: () => void;
  onError?: (e: Error) => void;
}

export const NBXCard = {
  init: (config: { baseUrl: string }) => ({ baseUrl } = config),

  getCards: async (): Promise<Card[]> => {
    const { account, user } = await contexts();
    return get(buildUrl(account, user, '/cards'), account) as Promise<Card[]>;
  },

  getCardAccount: () => context.account().then(account => get(`${baseUrl}/accounts/${account.id}`, account)),

  getCardAccountUser: () =>
    context
      .account()
      .then(account =>
        context.user().then(user => get(`${baseUrl}/accounts/${account.id}/users/${user.id}`, account))
      ),

  getBalance: () =>
    context.account().then(account => get(`${baseUrl}/accounts/${account.id}/balance`, account)),

  tokenize: async (panLast4: string): Promise<TokenizeResponse> => {
    const { account, user } = await contexts();
    return post(buildUrl(account, user, '/cards/tokenize'), { json: { panLast4 } }, account, {
      decamelizeRequestKeys: false
    }) as Promise<TokenizeResponse>;
  },

  tokenizePushProvision: async ({
    cardId,
    vendor,
    params
  }: TokenizePushProvisionParams): Promise<TokenizePushProvisionResponse> => {
    const { account, user } = await contexts();
    return post(
      buildUrl(account, user, `/cards/${cardId}/tokenize_push_provision`),
      { json: { vendor, params } },
      account,
      {
        decamelizeRequestKeys: false
      }
    ) as Promise<TokenizePushProvisionResponse>;
  },

  tokenizationStarted: async ({ cardId, vendor }: TokenizationStartedParams): Promise<void> => {
    const { account, user } = await contexts();
    post(buildUrl(account, user, `/cards/${cardId}/wallet/${vendor}/tokenization_started`), {}, account);
  }
};
